import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { IProviderProps } from "../interfaces/provider";
import { apiContext } from "./api";
import { authContext } from "./auth";
import { IBoardContextState, IRegionRecord } from "../interfaces/boardContext";
import { ISiteLocation } from "../interfaces/resource";
import {
    BOARD_CONTEXT_ID_STORAGE_KEY,
    BOARD_CONTEXT_TYPE_STORAGE_KEY,
    BOARD_PENDING_QUERY_SELECTION_STORAGE_KEY,
    BOARD_SELECTION_STORAGE_KEY,
    LEGACY_SITE_STORAGE_KEY,
    IBoardSelectionInput,
    buildContextKey,
    buildDraftContextId,
    buildSelectionKey,
    buildSelectionQueryString,
    normalizeSiteIdList,
    parseSelectionFromSearch,
    parseSelectionFromStorage,
    selectionCoversAllSites,
    serializeSelectionForStorage,
} from "../helpers/boardSelection";

type ResolvedBoardSelection = {
    selectionMode: "site" | "region";
    contextId: string;
    contextLabel: string;
    selectedSiteIds: string[];
    selectedSites: ISiteLocation[];
    allSites: ISiteLocation[];
};

const defaultBoardContextState: IBoardContextState = {
    loading: false,
    ready: false,
    hasActiveSelection: false,
    selectorOpen: false,
    selectorBlocking: false,
    selectionMode: 'site',
    contextType: 'site',
    contextId: '',
    contextKey: '',
    selectionKey: '',
    draftContextId: '',
    contextLabel: '',
    isRegion: false,
    regions: [],
    availableSites: [],
    allSites: [],
    selectedSiteIds: [],
    sites: [],
    siteIds: [],
    primarySite: undefined,
    siteById: () => undefined,
    openSelector: () => { },
    closeSelector: () => { },
    applySelection: () => false,
};

const boardContext = createContext<IBoardContextState>(defaultBoardContextState);

const sortByName = <TItem extends { illumina_name?: string }>(items: TItem[]): TItem[] => {
    return [...items].sort((left, right) => String(left.illumina_name ?? '').localeCompare(String(right.illumina_name ?? '')));
};

const getRegionKey = (site: ISiteLocation) => String(site._illumina_region_value ?? '').trim();

const BoardContextProvider = (props: IProviderProps) => {
    const { get } = useContext(apiContext);
    const { isAuthed } = useContext(authContext);
    const [state, setState] = useState<IBoardContextState>(defaultBoardContextState);

    const availableSitesRef = useRef<ISiteLocation[]>([]);
    const regionsRef = useRef<IRegionRecord[]>([]);
    const siteLookupRef = useRef<Map<string, ISiteLocation>>(new Map());
    const regionLookupRef = useRef<Map<string, IRegionRecord>>(new Map());
    const sitesByRegionRef = useRef<Map<string, ISiteLocation[]>>(new Map());

    const siteById = useCallback((siteId?: string | null) => {
        const normalizedSiteId = String(siteId ?? '').trim();
        if (!normalizedSiteId) {
            return undefined;
        }

        return siteLookupRef.current.get(normalizedSiteId);
    }, []);

    const buildActiveState = useCallback((selection: ResolvedBoardSelection): IBoardContextState => {
        const allSiteIds = selection.allSites.map((site) => site.illumina_sitelocationid);
        const selectedSiteIds = normalizeSiteIdList(selection.selectedSiteIds);
        const primarySite = selection.selectedSites[0];

        return {
            loading: false,
            ready: true,
            hasActiveSelection: true,
            selectorOpen: false,
            selectorBlocking: false,
            selectionMode: selection.selectionMode,
            contextType: selection.selectionMode,
            contextId: selection.contextId,
            contextKey: buildContextKey(selection.selectionMode, selection.contextId),
            selectionKey: buildSelectionKey(selection.selectionMode, selection.contextId, selectedSiteIds, allSiteIds),
            draftContextId: buildDraftContextId(selection.selectionMode, selection.contextId, selectedSiteIds, allSiteIds),
            contextLabel: selection.contextLabel,
            isRegion: selection.selectionMode === 'region',
            regions: regionsRef.current,
            availableSites: availableSitesRef.current,
            allSites: selection.allSites,
            selectedSiteIds,
            sites: selection.selectedSites,
            siteIds: selectedSiteIds,
            primarySite,
            siteById,
            openSelector: () => { },
            closeSelector: () => { },
            applySelection: () => false,
        };
    }, [siteById]);

    const buildEmptyState = useCallback((selectorOpen: boolean, selectorBlocking: boolean): IBoardContextState => {
        return {
            ...defaultBoardContextState,
            ready: true,
            hasActiveSelection: false,
            selectorOpen,
            selectorBlocking,
            regions: regionsRef.current,
            availableSites: availableSitesRef.current,
            siteById,
        };
    }, [siteById]);

    const persistSelection = useCallback((selection: ResolvedBoardSelection) => {
        const allSiteIds = selection.allSites.map((site) => site.illumina_sitelocationid);
        const normalizedSelectedSiteIds = normalizeSiteIdList(selection.selectedSiteIds);
        const normalizedSelection: IBoardSelectionInput = selection.selectionMode === 'region' && !selectionCoversAllSites(normalizedSelectedSiteIds, allSiteIds)
            ? {
                selectionMode: selection.selectionMode,
                contextId: selection.contextId,
                selectedSiteIds: normalizedSelectedSiteIds,
            }
            : {
                selectionMode: selection.selectionMode,
                contextId: selection.contextId,
            };

        localStorage.setItem(BOARD_CONTEXT_TYPE_STORAGE_KEY, selection.selectionMode);
        localStorage.setItem(BOARD_CONTEXT_ID_STORAGE_KEY, selection.contextId);
        localStorage.setItem(BOARD_SELECTION_STORAGE_KEY, serializeSelectionForStorage(normalizedSelection));
        localStorage.removeItem(BOARD_PENDING_QUERY_SELECTION_STORAGE_KEY);

        if (selection.selectionMode === 'site') {
            localStorage.setItem(LEGACY_SITE_STORAGE_KEY, selection.contextId);
        } else {
            localStorage.removeItem(LEGACY_SITE_STORAGE_KEY);
        }

        const nextQueryString = buildSelectionQueryString(
            window.location.search,
            selection.selectionMode,
            selection.contextId,
            normalizedSelectedSiteIds,
            allSiteIds,
        );
        const nextRelativeUrl = `${window.location.pathname}${nextQueryString ? `?${nextQueryString}` : ''}${window.location.hash}`;
        const currentRelativeUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        if (nextRelativeUrl !== currentRelativeUrl) {
            window.history.replaceState(null, '', nextRelativeUrl);
        }
    }, []);

    const clearStoredSelection = useCallback(() => {
        localStorage.removeItem(BOARD_CONTEXT_TYPE_STORAGE_KEY);
        localStorage.removeItem(BOARD_CONTEXT_ID_STORAGE_KEY);
        localStorage.removeItem(BOARD_SELECTION_STORAGE_KEY);
        localStorage.removeItem(BOARD_PENDING_QUERY_SELECTION_STORAGE_KEY);
        localStorage.removeItem(LEGACY_SITE_STORAGE_KEY);
    }, []);

    const resolveSelection = useCallback((selection: IBoardSelectionInput | null | undefined): ResolvedBoardSelection | null => {
        const selectionMode = selection?.selectionMode === 'region' ? 'region' : 'site';
        const contextId = String(selection?.contextId ?? '').trim();
        if (!contextId) {
            return null;
        }

        if (selectionMode === 'site') {
            const site = siteLookupRef.current.get(contextId);
            if (!site) {
                return null;
            }

            return {
                selectionMode,
                contextId,
                contextLabel: String(site.illumina_name ?? 'Site Location').trim() || 'Site Location',
                selectedSiteIds: [site.illumina_sitelocationid],
                selectedSites: [site],
                allSites: [site],
            };
        }

        const region = regionLookupRef.current.get(contextId);
        const regionSites = sitesByRegionRef.current.get(contextId) ?? [];
        if (!region || regionSites.length < 1) {
            return null;
        }

        const regionSiteIds = regionSites.map((site) => site.illumina_sitelocationid);
        const requestedSiteIds = normalizeSiteIdList(selection?.selectedSiteIds ?? []);
        const selectedSiteIds = requestedSiteIds.length > 0
            ? regionSiteIds.filter((siteId) => requestedSiteIds.includes(siteId))
            : regionSiteIds;
        if (selectedSiteIds.length < 1) {
            return null;
        }

        const selectedSiteIdSet = new Set(selectedSiteIds);
        const selectedSites = regionSites.filter((site) => selectedSiteIdSet.has(site.illumina_sitelocationid));

        return {
            selectionMode,
            contextId,
            contextLabel: String(region.illumina_name ?? 'Region').trim() || 'Region',
            selectedSiteIds,
            selectedSites,
            allSites: regionSites,
        };
    }, []);

    const applySelection = useCallback((selection: IBoardSelectionInput) => {
        const resolvedSelection = resolveSelection(selection);
        if (!resolvedSelection) {
            return false;
        }

        persistSelection(resolvedSelection);
        setState(buildActiveState(resolvedSelection));
        return true;
    }, [buildActiveState, persistSelection, resolveSelection]);

    const openSelector = useCallback(() => {
        setState((previous) => ({
            ...previous,
            selectorOpen: true,
            selectorBlocking: !previous.hasActiveSelection,
        }));
    }, []);

    const closeSelector = useCallback(() => {
        setState((previous) => {
            if (!previous.hasActiveSelection) {
                return previous;
            }

            return {
                ...previous,
                selectorOpen: false,
                selectorBlocking: false,
            };
        });
    }, []);

    useEffect(() => {
        if (!isAuthed) {
            return;
        }

        let cancelled = false;
        const loadContextOptions = async () => {
            if (!cancelled) {
                setState((previous) => ({
                    ...previous,
                    loading: true,
                    ready: false,
                }));
            }

            try {
                const [regionResponse, siteResponse] = await Promise.all([
                    get('/Entity/illumina_regions', {
                        select: 'illumina_name,illumina_regionid',
                        filter: 'statecode eq 0',
                        top: 5000,
                    }) as Promise<{ value?: IRegionRecord[] }>,
                    get('/Entity/illumina_sitelocations', {
                        select: 'illumina_name,illumina_sitelocationid,illumina_addressline1,illumina_addresscity,illumina_addressstateorprovince,illumina_addresspostalcode,illumina_latitudes,illumina_longitudes,_illumina_region_value',
                        filter: 'statecode eq 0',
                        top: 5000,
                    }) as Promise<{ value?: ISiteLocation[] }>,
                ]);

                const availableSites = sortByName(Array.isArray(siteResponse?.value) ? siteResponse.value : []);
                const regionSiteMap = new Map<string, ISiteLocation[]>();
                availableSites.forEach((site) => {
                    const regionId = getRegionKey(site);
                    if (!regionId) {
                        return;
                    }

                    const currentSites = regionSiteMap.get(regionId) ?? [];
                    currentSites.push(site);
                    regionSiteMap.set(regionId, currentSites);
                });

                regionSiteMap.forEach((sites, regionId) => {
                    regionSiteMap.set(regionId, sortByName(sites));
                });

                const regions = sortByName((Array.isArray(regionResponse?.value) ? regionResponse.value : [])
                    .map((region) => ({
                        ...region,
                        siteCount: (regionSiteMap.get(String(region.illumina_regionid ?? '').trim()) ?? []).length,
                    }))
                    .filter((region) => region.siteCount && region.siteCount > 0));

                availableSitesRef.current = availableSites;
                regionsRef.current = regions;
                siteLookupRef.current = new Map(availableSites.map((site) => [site.illumina_sitelocationid, site]));
                regionLookupRef.current = new Map(regions.map((region) => [String(region.illumina_regionid ?? '').trim(), region]));
                sitesByRegionRef.current = regionSiteMap;

                const candidateSelections: IBoardSelectionInput[] = [];
                const explicitSelection = parseSelectionFromSearch(window.location.search);
                const pendingQuerySelection = parseSelectionFromStorage(localStorage.getItem(BOARD_PENDING_QUERY_SELECTION_STORAGE_KEY));
                const storedSelection = parseSelectionFromStorage(localStorage.getItem(BOARD_SELECTION_STORAGE_KEY));
                const legacySiteId = String(localStorage.getItem(LEGACY_SITE_STORAGE_KEY) ?? '').trim();

                if (explicitSelection) {
                    candidateSelections.push(explicitSelection);
                }
                if (pendingQuerySelection) {
                    candidateSelections.push(pendingQuerySelection);
                }
                if (storedSelection) {
                    candidateSelections.push(storedSelection);
                }
                if (legacySiteId) {
                    candidateSelections.push({
                        selectionMode: 'site',
                        contextId: legacySiteId,
                    });
                }

                const resolvedSelection = candidateSelections
                    .map((candidate) => resolveSelection(candidate))
                    .find((candidate): candidate is ResolvedBoardSelection => Boolean(candidate));

                if (cancelled) {
                    return;
                }

                if (resolvedSelection) {
                    persistSelection(resolvedSelection);
                    setState(buildActiveState(resolvedSelection));
                    return;
                }

                clearStoredSelection();
                setState(buildEmptyState(true, true));
            } catch (error) {
                console.error('Failed to load board context options.', error);
                if (cancelled) {
                    return;
                }

                clearStoredSelection();
                setState(buildEmptyState(true, true));
            }
        };

        loadContextOptions();

        return () => {
            cancelled = true;
        };
    }, [buildActiveState, buildEmptyState, clearStoredSelection, get, isAuthed, persistSelection, resolveSelection]);

    const value = useMemo(() => ({
        ...state,
        siteById,
        openSelector,
        closeSelector,
        applySelection,
    }), [applySelection, closeSelector, openSelector, siteById, state]);

    return (
        <boardContext.Provider value={value}>
            {props.children}
        </boardContext.Provider>
    );
};

export {
    boardContext,
    BoardContextProvider,
};
